import {
    IEntity,
    IEntityAttribute,
    IEntityAttributeChangeEvent,
    IEntityAttributeSearchOptions
} from "../PA.MD.Core/PA.MD.IF.Entity";
import {
    WB_AttributeTypeRow,
    WB_CustomerAttributeRow,
    WB_CustomerRow, WB_CustomerSalesAreaRow,
    WB_CustomerTypeRow,
    WB_EntityRow,
    WB_SalesAreaRow
} from "../PA.Core/PAdbSchemaRows";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {PAdbContext} from "../PA.Core/PAdbContext";
import * as _ from "lodash"
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {
    esWB_Customer,
    esWB_Customer_Attribute,
    esWB_Customer_Role_Customer_Relationship,
    esWB_Customer_Role_Relationship,
    esWB_Customer_SalesArea_Relationship,
    esWB_SalesArea
} from "../PA.Core/PAdbSchema";
import {EntityTypeUtils} from "./EntityTypeUtils";
import {BehaviorSubject} from "rxjs";
import {CustomAttribute} from "./CustomAttribute";
import {CoreAttribute} from "./CoreAttribute";
import {EntityUtils} from "./EntityUtils";

const dctSalesAreaFieldsByMapper = {
    [esWB_Customer_SalesArea_Relationship]: "WB_SALESAREA_ID",
    [esWB_Customer_Role_Relationship]: "WB_SALESAREA_ID",
    [esWB_Customer_Role_Customer_Relationship]: "WB_SALESAREA_ID"
}

export class CustomerEntity
    implements IEntity {
    constructor(customerRow: WB_CustomerRow) {
        this.customerRow = customerRow
        this._dbContext = <PAdbContext>customerRow.dataStore()
        this.loadMessageProvider = new MessageProvider()
        this.validateMessageProvider = new MessageProvider()
        this._bLoaded = false
        this._bSaved = false
        this._bDeleted = false
        this.onLoadComplete = undefined
        this.dctAttributeMByWbId = {}
        this.dctAttributeMById = {}
        this.dctAttributesByAttrMId = {}
        this.dctCustMByWbId = {}
        this.dctEntityMByWbId = {}
        this.entityAttributes = []
        this.entityAttributesChanged = new BehaviorSubject<IEntityAttributeChangeEvent>(undefined)
    }

    private customerRow: WB_CustomerRow
    private _dbContext: PAdbContext
    private loadMessageProvider: MessageProvider
    private entityAttributes: IEntityAttribute[];
    private validateMessageProvider: MessageProvider
    private _bLoaded: boolean
    private _bDeleted: boolean;
    private _bSaved: boolean;
    private onLoadComplete: Promise<void>
    private dctCustMByWbId: { [wbId: number]: WB_CustomerTypeRow }
    private dctEntityMByWbId: { [wbId: number]: WB_EntityRow }

    private dctAttributeMByWbId: { [wbId: number]: WB_AttributeTypeRow }
    private dctAttributeMById: { [attrMId: string]: WB_AttributeTypeRow }
    private dctAttributesByAttrMId: { [attrMId: string]: IEntityAttribute[] }
    private entityAttributesChanged: BehaviorSubject<IEntityAttributeChangeEvent>

    get dbContext(): PAdbContext {
        return this._dbContext
    }

    get wbMasterDataSetId(): number {
        return this.customerRow.cif.WB_MDSET_ID
    }

    get wb_id(): number {
        return this.customerRow.cif.WB_CUST_ID
    }

    get id_ui(): string {
        return this.customerRow?.cif?.CUSTOMER_ID
    }

    get key_set(): string {
        return this.customerRow?.cif?.CUSTOMER_SET_ID
    }

    get name(): string {
        return this.customerRow?.cif?.CUSTOMER_NAME
    }

    get mapperName(): string {
        return esWB_Customer
    }

    get LoadMessageProvider(): MessageProvider {
        return this.loadMessageProvider
    };

    get EntityAttributesChanged(): BehaviorSubject<IEntityAttributeChangeEvent> {
        return this.entityAttributesChanged
    }

    OnLoadComplete(): Promise<void> {
        const self = this
        if (!_.isUndefined(this.onLoadComplete))
            return this.onLoadComplete

        let rv = (async () => {

            try {
                _.forEach(self.customerRow.WB_CustomerRoleRelationshipRows, (a) => {
                    let ct = a.WB_CustomerRoleRow?.WB_CustomerTypeRow
                    if (_.isEmpty(ct))
                        return
                    let wbId = ct.cif.WB_CUSTM_ID
                    let Id = ct.cif.CUSTM_ID
                    if (!(wbId in self.dctCustMByWbId))
                        self.dctCustMByWbId[wbId] = ct
                })

                for (let wbCustMId of _.keys(self.dctCustMByWbId)) {
                    // Get the Entity
                    let et = await EntityTypeUtils.getEntityTypeRowByMeta("WB_CustM", +wbCustMId)
                    if (!_.isEmpty(et))
                        self.dctEntityMByWbId[et.cif.WB_ENTITY_ID] = et
                }

                // Get the Attributes..
                for (let entityRow of _.values(self.dctEntityMByWbId)) {
                    let arrAtt = await EntityTypeUtils.getEntityAttributeTypes(entityRow.cif.WB_ENTITY_ID)

                    _.forEach(arrAtt, (a) => {
                        let wbId = a.cif.WB_ATTRIBUTEM_ID
                        let Id = a.cif.ATTRIBUTEM_ID
                        if (!(wbId in self.dctAttributeMByWbId)) {
                            self.dctAttributeMByWbId[wbId] = a
                        }

                        if (!(Id in self.dctAttributeMById)) {
                            self.dctAttributeMById[Id] = a
                        }

                    })
                }

                let coreAttrs = await EntityTypeUtils.getCoreAttributeTypes("Customer")
                _.forEach(coreAttrs, (a) => {
                    let wbId = a.cif.WB_ATTRIBUTEM_ID
                    let Id = a.cif.ATTRIBUTEM_ID
                    if (!(wbId in self.dctAttributeMByWbId)) {
                        self.dctAttributeMByWbId[wbId] = a
                    }

                    if (!(Id in self.dctAttributeMById)) {
                        self.dctAttributeMById[Id] = a
                    }

                })


                // Create Custom Attributes
                _.forEach(self.customerRow.WB_CustomerAttributeRows, (a) => {
                    if (!a.isActive)
                        return
                    let at = a.WB_AttributeTypeRow
                    if (_.isEmpty(at))
                        return
                    let ca = new CustomAttribute(self, at, a)

                    self.entityAttributes.push(ca)
                    self.indexAttribute(ca)
                })

                // Create Core Attributes
                for (let coreAttrM of coreAttrs) {
                    //-- Use the Path to determine the dataRecords.
                    let startMapper = coreAttrM.cif.CORE_STARTMAPPER
                    let rowMapper = coreAttrM.cif.CORE_ROWMAPPER
                    let rowFieldName = coreAttrM.cif.CORE_VALUE_FIELDNAME
                    let path = coreAttrM.cif.CORE_PATH.trim()
                    if (startMapper != esWB_Customer)
                        continue
                    let tgtEntity: WB_EntityRow = undefined
                    if (coreAttrM.cif.DATATYPE == "entity") {
                        tgtEntity = await EntityTypeUtils.getEntityTypeRowByLogicalKey(self.wbMasterDataSetId, coreAttrM.cif.ENTITY_DIMENSION, coreAttrM.cif.CORE_ENTITY_ID_TARGET)
                    }

                    if ((rowMapper == startMapper) && (_.isEmpty(path))) {
                        let ca = new CoreAttribute(self, coreAttrM, self.customerRow, tgtEntity, rowFieldName)
                        self.entityAttributes.push(ca)
                        this.indexAttribute(ca)
                        continue
                    }

                    // findDataRows related to Path, may be one

                    let arrPathItems = path.split(".")

                    function findRows(searchRows: ESRecord[], pPath: string[]): ESRecord[] {
                        if (_.isEmpty(pPath))
                            return searchRows
                        let nextRows: ESRecord[] = []
                        let fieldName = pPath.shift()
                        _.forEach(searchRows, (a) => {
                            let fieldVal = (<any>a)[fieldName]
                            if (_.isArray(fieldVal)) {
                                _.forEach(fieldVal, (aRow: ESRecord) => {
                                    if (!_.isObject(aRow))
                                        return
                                    if (aRow.isActive)
                                        nextRows.push(aRow)
                                })
                            } else if (_.isObjectLike(fieldVal)) {
                                if (!fieldVal.isActive)
                                    return
                                nextRows.push(fieldVal)
                            } else {
                                // Element not found
                            }
                        })

                        return findRows(nextRows, _.clone(pPath))
                    }

                    let foundRows = findRows([self.customerRow], arrPathItems)
                    for (let aRow of foundRows) {
                        let aCoreAttr = new CoreAttribute(self, coreAttrM, aRow, tgtEntity,
                            coreAttrM.cif.CORE_VALUE_FIELDNAME,
                            coreAttrM.cif.CORE_SALESAREA_FIELDNAME,
                            coreAttrM.cif.CORE_STARTDT_FIELDNAME,
                            coreAttrM.cif.CORE_ENDDT_FIELDNAME)
                        self.entityAttributes.push(aCoreAttr)
                        this.indexAttribute(aCoreAttr)

                    }
                }


                self._bLoaded = true
            } catch (err) {
                self._bLoaded = false
            }
        })();

        return rv
    }

    get bLoaded(): boolean {
        return this._bLoaded
    }

    get EntityAttributes(): IEntityAttribute[] {
        return this.entityAttributes
    }

    get AttributeTypes(): WB_AttributeTypeRow[] {
        return _.values(this.dctAttributeMByWbId)
    }

    async createAttribute(attrType: WB_AttributeTypeRow | number): Promise<IEntityAttribute> {
        let wbAttrMRow: WB_AttributeTypeRow = undefined
        if (_.isNumber(attrType) || _.isString(attrType)) {
            wbAttrMRow = this.dctAttributeMByWbId[<any>attrType]
        }
        if (_.isEmpty(wbAttrMRow) && !_.isEmpty(attrType)) {
            wbAttrMRow = <any>attrType
        }

        if (_.isEmpty(wbAttrMRow) || !wbAttrMRow?.isActive)
            return

        if (wbAttrMRow.bCoreInd)
            return

        let aNewRow: WB_CustomerAttributeRow = <any>await this.dbContext.createRecordWithKey(esWB_Customer_Attribute)
        aNewRow.setDefaults()
        aNewRow.cif.WB_CUST_ID = this.customerRow.cif.WB_CUST_ID
        aNewRow.cif.WB_ATTRIBUTEM_ID = wbAttrMRow.cif.WB_ATTRIBUTEM_ID
        aNewRow.WB_CustomerRow = this.customerRow
        aNewRow.WB_AttributeTypeRow = wbAttrMRow
        aNewRow.cif.WB_SALESAREA_ID = 0
        this.dbContext.add(esWB_Customer_Attribute, aNewRow)

        let ea = new CustomAttribute(this, wbAttrMRow, aNewRow)
        this.entityAttributes.push(ea)
        this.indexAttribute(ea)
        this.EntityAttributesChanged.next({Entity: this, Attribute: ea})
        return ea
    }


    getEntityAttribute(attributeTypeId: string, options?: IEntityAttributeSearchOptions): IEntityAttribute {
        return EntityUtils.searchAttributes(this,
            this.dctAttributesByAttrMId[attributeTypeId] || [],
            options)
    }

    private indexAttribute(ea: IEntityAttribute): void {
        if (_.isEmpty(ea) || _.isEmpty(ea.type))
            return

        let attrMId = ea.type.cif.ATTRIBUTEM_ID
        if (!(attrMId in this.dctAttributesByAttrMId))
            this.dctAttributesByAttrMId[attrMId] = []

        this.dctAttributesByAttrMId[attrMId].push(ea)
    }


    get ValidateMessageProvider(): MessageProvider {
        return this.validateMessageProvider
    };

    async Validate(): Promise<void> {
        this.ValidateMessageProvider.clearMessages()

        // Call re-usable validation that checks for duplicate attributes
        await EntityUtils.ValidateCustomAttributes(this)
    }

    async Save(bBatch?: boolean, options?: { bSkipValidations?: boolean, dbContext?: PAdbContext }): Promise<void> {
        const self = this
        let dbContext = options?.dbContext || this._dbContext

        let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
        if (bBatch) {
            saveFunction = dbContext.SaveBatch.bind(dbContext)
        } else {
            saveFunction = dbContext.Save.bind(dbContext)
        }

        self.ValidateMessageProvider.clearMessages()
        if (!(options?.bSkipValidations))
            await self.Validate()

        if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
            self._bSaved = false;
            return
        }
        // Just Save the Custom Attributes
        await saveFunction(esWB_Customer, self.customerRow)
        await saveFunction(esWB_Customer_Attribute, self.customerRow.WB_CustomerAttributeRows)
        await saveFunction(esWB_Customer_SalesArea_Relationship, self.customerRow.WB_CustomerSalesAreaRows)

        if (bBatch) {
            dbContext.addBatchCompleteListener(() => {
                self._bSaved = true;
            })
        } else {
            self._bSaved = true;
        }

    }

    get bSaved(): boolean {
        return this._bSaved
    }


    async Delete(bSaveNow: boolean, options?: { bSkipValidations?: boolean }): Promise<void> {
        // Do nothing, only delete through the BO..
        this._bDeleted = true
    }

    get bDeleted(): boolean {
        return this._bDeleted
    }

    setName(sName: string) {
        this.customerRow.cif.CUSTOMER_NAME = sName
    }

    get bHasSalesAreas(): boolean {
        return true
    }

    get SalesAreas(): WB_SalesAreaRow[] {
        return _.map(_.filter(this.customerRow.WB_CustomerSalesAreaRows, (a) => a.isActive && !_.isEmpty(a.WB_SalesAreaRow)),
            aRow => aRow.WB_SalesAreaRow
        )
    }

    async addSalesArea(wbSalesAreaId: number): Promise<void> {
        if (_.filter(this.SalesAreas, sa => sa.cif.WB_SALESAREA_ID == wbSalesAreaId).length)
            return
        let aSARRow: WB_CustomerSalesAreaRow = <WB_CustomerSalesAreaRow>await this._dbContext.createRecordWithKey(esWB_Customer_SalesArea_Relationship)
        let aSARow: WB_SalesAreaRow = await this.dbContext.find(esWB_SalesArea, wbSalesAreaId)
        aSARRow.setDefaults()
        aSARRow.cif.WB_CUST_ID = this.customerRow.cif.WB_CUST_ID
        aSARRow.WB_CustomerRow = this.customerRow
        aSARRow.cif.WB_SALESAREA_ID = wbSalesAreaId
        aSARRow.WB_SalesAreaRow = aSARow
        await this.dbContext.add(esWB_Customer_SalesArea_Relationship, aSARRow)
    }

    removeSalesArea(wbSalesAreaId: number): Promise<void> {
        for (let row of this.customerRow.WB_CustomerSalesAreaRows) {
            if (row.cif.WB_SALESAREA_ID == wbSalesAreaId)
                row.markDeleted()
        }
        return

    }


}